<template>
  <div class="advanced-filter-drawer-shell">
    <el-drawer
      v-model="drawerVisible"
      :close-on-click-modal="true"
      direction="rtl"
      :destroy-on-close="destroyOnClose"
      :size="drawerSize">
      <template #title>
        <div class="drawer-title">
          {{ title }}
        </div>
      </template>
      <template #default>
        <div v-loading="loading" class="advanced-filter-drawer">
          <section v-if="filterFieldList.length" class="field-section">
            <div class="section-title section-title--with-icon">
              <el-icon><Filter /></el-icon>
              <span>{{ filterSectionTitle }}</span>
            </div>
            <div class="field-list">
              <div v-for="field in filterFieldList" :key="field.key" class="field-item">
                <div class="field-label">
                  {{ field.label }}
                </div>
                <div class="field-control">
                  <select-number-field
                    v-if="field.type === 'select-number'"
                    :field="field"
                    :choose-placeholder="defaultChoosePlaceholder"
                    :search-placeholder="t('placeholder.search')"
                    :teleported="teleported"
                    @update-field="updateField" />
                  <component
                    :is="resolveFieldComponent(field)"
                    v-else-if="field.type !== 'slot'"
                    v-model="field.value"
                    v-bind="getFieldProps(field)"
                    @change="handleFieldChange(field)"
                    @click="handleTextFieldClick(field)">
                    <template v-if="field.type === 'select' || field.type === 'select-multiple'">
                      <el-option
                        v-for="option in field.options || []"
                        :key="`${field.key}-${option.value}`"
                        :label="option.label"
                        :value="option.value" />
                    </template>
                  </component>
                  <slot
                    v-else
                    :name="field.key"
                    :field="field"
                    :field-key="field.key"
                    :update-field="updateField"
                    :clear-field="clearField" />
                </div>
                <div class="field-action">
                  <el-button
                    v-if="hasFieldValue(field)"
                    text
                    class="field-clear-btn"
                    @click="clearField(field.key)">
                    <el-icon><Close /></el-icon>
                  </el-button>
                </div>
              </div>
            </div>
          </section>

          <section v-if="orderFieldList.length" class="field-section">
            <div class="section-title">
              {{ orderSectionTitle }}
            </div>
            <div class="field-list">
              <div v-for="field in orderFieldList" :key="field.key" class="field-item">
                <div class="field-label">
                  {{ field.label }}
                </div>
                <div class="field-control">
                  <el-select
                    v-model="field.value"
                    :clearable="field.clearable !== false"
                    :placeholder="field.placeholder || defaultChoosePlaceholder"
                    :teleported="teleported">
                    <el-option
                      v-for="option in field.options || []"
                      :key="`${field.key}-${option.value}`"
                      :label="option.label"
                      :value="option.value" />
                  </el-select>
                </div>
                <div class="field-action">
                  <el-button
                    v-if="hasFieldValue(field)"
                    text
                    class="field-clear-btn"
                    @click="clearField(field.key)">
                    <el-icon><Close /></el-icon>
                  </el-button>
                </div>
              </div>
            </div>
          </section>

          <section v-if="otherFieldList.length || hasOtherCondition" class="field-section">
            <div class="section-title">
              {{ otherSectionTitle }}
            </div>
            <div class="field-list other-field-list">
              <div v-for="field in otherFieldList" :key="field.key" class="field-item">
                <div class="field-label">
                  {{ field.label }}
                </div>
                <div class="field-control">
                  <select-number-field
                    v-if="field.type === 'select-number'"
                    :field="field"
                    :choose-placeholder="defaultChoosePlaceholder"
                    :search-placeholder="t('placeholder.search')"
                    :teleported="teleported"
                    @update-field="updateField" />
                  <component
                    :is="resolveFieldComponent(field)"
                    v-else-if="field.type !== 'slot'"
                    v-model="field.value"
                    v-bind="getFieldProps(field)"
                    @change="handleFieldChange(field)"
                    @click="handleTextFieldClick(field)">
                    <template v-if="field.type === 'select' || field.type === 'select-multiple'">
                      <el-option
                        v-for="option in field.options || []"
                        :key="`${field.key}-${option.value}`"
                        :label="option.label"
                        :value="option.value" />
                    </template>
                  </component>
                  <slot
                    v-else
                    :name="field.key"
                    :field="field"
                    :field-key="field.key"
                    :update-field="updateField"
                    :clear-field="clearField" />
                </div>
                <div class="field-action">
                  <el-button
                    text
                    class="field-clear-btn field-remove-btn"
                    @click="removeOtherField(field.key)">
                    <el-icon><Close /></el-icon>
                  </el-button>
                </div>
              </div>
              <el-button
                v-if="hasOtherCondition"
                class="advanced-add-condition-btn"
                type="primary"
                plain
                @click="addOtherDialogVisible = true">
                <el-icon><Plus /></el-icon>
                <span>{{ addConditionText }}</span>
              </el-button>
            </div>
          </section>
        </div>
      </template>
      <template #footer>
        <div class="drawer-footer">
          <el-button @click="handleCancel">
            {{ cancelText }}
          </el-button>
          <el-button @click="handleReset">
            {{ resetText }}
          </el-button>
          <el-button type="primary" @click="handleSubmit">
            {{ submitText }}
          </el-button>
        </div>
      </template>
    </el-drawer>

    <el-dialog
      v-model="addOtherDialogVisible"
      :title="addConditionText"
      width="900px"
      append-to-body>
      <el-checkbox-group v-model="selectedOtherKeys" class="other-condition-list">
        <el-checkbox
          v-for="field in availableOtherFieldList"
          :key="field.key"
          :label="field.key">
          {{ field.label }}
        </el-checkbox>
      </el-checkbox-group>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="closeOtherDialog">
            {{ cancelText }}
          </el-button>
          <el-button type="primary" @click="confirmAddOtherFields">
            {{ submitText }}
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
// 列表页通用高级筛选抽屉，字段由 filterCondition 配置驱动
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Close, Filter, Plus } from '@element-plus/icons-vue'
import { ElMessageBox } from 'element-plus'
import SelectNumberField from './SelectNumberField.vue'

const props = defineProps({
  visible: Boolean,
  title: {
    type: String,
    default: ''
  },
  width: {
    type: [String, Number],
    default: '520px'
  },
  loading: Boolean,
  teleported: {
    type: Boolean,
    default: true
  },
  destroyOnClose: {
    type: Boolean,
    default: true
  },
  hasOtherCondition: {
    type: Boolean,
    default: true
  },
  labelJoiner: {
    type: String,
    default: ': '
  },
  filterCondition: {
    type: Object,
    default: () => ({})
  },
  otherConditionList: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits([
  'update:visible',
  'update:filterCondition',
  'submit',
  'cancel',
  'openSearchWin',
  'loadOptions',
  'clearSpecialField'
])

const { t } = useI18n()

const filterForm = reactive({
  filter: {},
  order: {},
  other: {}
})

const addOtherDialogVisible = ref(false)
const selectedOtherKeys = ref([])

const drawerVisible = computed({
  get: () => props.visible,
  set: value => emit('update:visible', value)
})

const drawerSize = computed(() => {
  return typeof props.width === 'number' ? `${props.width}px` : props.width
})

const filterFieldList = computed(() => Object.values(filterForm.filter))
const orderFieldList = computed(() => Object.values(filterForm.order))
const otherFieldList = computed(() => Object.values(filterForm.other))

const filterSectionTitle = computed(() => t('table.filter'))
const orderSectionTitle = computed(() => t('table.sorting'))
const otherSectionTitle = computed(() => t('table.moreConditions'))
const addConditionText = computed(() => t('table.addCondition'))
const resetText = computed(() => t('table.reset') || 'Reset')
const cancelText = computed(() => t('form.cancel'))
const submitText = computed(() => t('form.submit'))
const defaultChoosePlaceholder = computed(() => t('placeholder.choose'))

const availableOtherFieldList = computed(() => {
  return Object.values(props.otherConditionList).filter(field => !filterForm.other[field.key])
})

const cloneField = (field, seen = new WeakMap()) => {
  if (!field || typeof field !== 'object') return field
  if (seen.has(field)) return seen.get(field)
  if (field instanceof Date) return new Date(field.getTime())
  if (Array.isArray(field)) {
    const clonedArray = []
    seen.set(field, clonedArray)
    field.forEach(item => {
      clonedArray.push(cloneField(item, seen))
    })
    return clonedArray
  }

  const clonedField = {}
  seen.set(field, clonedField)

  Object.keys(field).forEach(key => {
    clonedField[key] = cloneField(field[key], seen)
  })

  return clonedField
}

const resetFieldValue = (field) => {
  if (field.type === 'select-number') {
    field.selectValue = field.defaultSelectValue || '>='
    field.submitValue = field.defaultSubmitValue ?? ''
    if (field.displayValue !== undefined) field.displayValue = ''
    return
  }
  if (Array.isArray(field.defaultValue)) {
    field.value = [...field.defaultValue]
    return
  }
  field.value = field.defaultValue ?? ''
  if (field.displayValue !== undefined) field.displayValue = ''
}

const buildFormByCondition = () => {
  filterForm.filter = {}
  filterForm.order = {}
  filterForm.other = {}
  Object.values(props.filterCondition || {}).forEach(field => {
    const clonedField = cloneField(field)
    if (clonedField.filterType === 'order') {
      filterForm.order[clonedField.key] = clonedField
      return
    }
    if (clonedField.filterType === 'other') {
      filterForm.other[clonedField.key] = clonedField
      return
    }
    filterForm.filter[clonedField.key] = clonedField
  })
}

const getFieldGroup = (fieldKey) => {
  if (filterForm.filter[fieldKey]) return filterForm.filter
  if (filterForm.order[fieldKey]) return filterForm.order
  if (filterForm.other[fieldKey]) return filterForm.other
  return null
}

const updateField = (fieldKey, patch = {}) => {
  const group = getFieldGroup(fieldKey)
  if (!group) return
  group[fieldKey] = {
    ...group[fieldKey],
    ...patch
  }
}

const clearField = (fieldKey) => {
  const group = getFieldGroup(fieldKey)
  if (!group || !group[fieldKey]) return
  resetFieldValue(group[fieldKey])
}

const hasFieldValue = (field) => {
  if (!field) return false
  const normalizedValue = normalizeFieldValue(field)
  if (!normalizedValue) return false
  return !isDefaultFieldValue(field, normalizedValue)
}

const closeOtherDialog = () => {
  addOtherDialogVisible.value = false
  selectedOtherKeys.value = []
}

const confirmAddOtherFields = () => {
  selectedOtherKeys.value.forEach(fieldKey => {
    if (!props.otherConditionList[fieldKey] || filterForm.other[fieldKey]) return
    filterForm.other[fieldKey] = cloneField(props.otherConditionList[fieldKey])
  })
  closeOtherDialog()
}

const doRemoveOtherField = (fieldKey) => {
  if (!filterForm.other[fieldKey]) return
  emit('clearSpecialField', fieldKey)
  delete filterForm.other[fieldKey]
}

const removeOtherField = async(fieldKey) => {
  const field = filterForm.other[fieldKey]
  if (!field) return
  if (!hasFieldValue(field)) {
    doRemoveOtherField(fieldKey)
    return
  }
  try {
    await ElMessageBox.confirm(t('table.sure_del'), t('table.remove'), {
      confirmButtonText: t('table.remove'),
      cancelButtonText: t('form.cancel'),
      type: 'warning'
    })
    doRemoveOtherField(fieldKey)
  } catch {
    return
  }
}

const resolveFieldComponent = (field) => {
  if (field.type === 'select' || field.type === 'select-multiple') return 'el-select'
  return 'el-input'
}

const getFieldProps = (field) => {
  if (field.type === 'select' || field.type === 'select-multiple') {
    return {
      multiple: field.type === 'select-multiple',
      clearable: field.clearable !== false,
      placeholder: field.placeholder || defaultChoosePlaceholder.value,
      filterable: field.filterable || false,
      disabled: field.disabled === true,
      collapseTags: field.type === 'select-multiple',
      loading: field.loading === true,
      teleported: props.teleported
    }
  }
  return {
    readonly: field.type === 'text',
    clearable: field.clearable !== false,
    placeholder: field.placeholder || t('placeholder.search'),
    disabled: field.disabled === true
  }
}

const handleTextFieldClick = (field) => {
  if (field.type === 'text') emit('openSearchWin', field)
}

const handleFieldChange = (field) => {
  if ((field.type === 'select' || field.type === 'select-multiple') && !field.optionsLoaded) {
    emit('loadOptions', field)
  }
}

const selectNumberOperatorMap = {
  '>=': 'gte',
  '<=': 'lte'
}

const selectNumberDisplayOperatorMap = {
  gte: '>=',
  lte: '<='
}

const normalizeFieldValue = (field) => {
  if (field.type === 'input') {
    if (typeof field.value === 'object' && field.value !== null) return ''
    const inputValue = field.value == null ? '' : String(field.value)
    return field.trim === false ? inputValue : inputValue.trim()
  }
  if (field.type === 'select-multiple' || field.type === 'slot') {
    if (Array.isArray(field.value)) {
      if (!field.value.length) return ''
      if (field.type === 'select-multiple' && field.value.includes('')) return ''
      const filtered = field.value.filter(item => item && item !== 'all_associated_policies')
      if (!filtered.length) return ''
      return filtered.join(',')
    }
    if (field.type === 'slot') {
      return field.value || ''
    }
    return ''
  }
  if (field.type === 'select-number') {
    const value = field.submitValue == null ? '' : String(field.submitValue).trim()
    if (!value) return ''
    if (!Number.isFinite(Number(value))) return ''
    const operator = field.selectValue || field.defaultSelectValue || '>='
    const normalizedOperator = selectNumberOperatorMap[operator]
    if (!normalizedOperator) return ''
    return `${normalizedOperator}:${value}`
  }
  if (field.type === 'text') {
    return field.value || ''
  }
  return field.value ?? ''
}

const isSameFieldValue = (value, defaultValue) => {
  if (Array.isArray(value) || Array.isArray(defaultValue)) {
    if (!Array.isArray(value) || !Array.isArray(defaultValue)) return false
    if (value.length !== defaultValue.length) return false
    return value.every((item, index) => item === defaultValue[index])
  }
  return value === defaultValue
}

const normalizeDefaultFieldValue = (field) => {
  if (field.type === 'select-number') {
    const value = field.defaultSubmitValue == null ? '' : String(field.defaultSubmitValue).trim()
    if (!value) return ''
    if (!Number.isFinite(Number(value))) return ''
    const operator = field.defaultSelectValue || '>='
    const normalizedOperator = selectNumberOperatorMap[operator]
    if (!normalizedOperator) return ''
    return `${normalizedOperator}:${value}`
  }
  if (!Object.prototype.hasOwnProperty.call(field, 'defaultValue')) return undefined
  if (field.type === 'input') {
    if (typeof field.defaultValue === 'object' && field.defaultValue !== null) return ''
    const inputValue = field.defaultValue == null ? '' : String(field.defaultValue)
    return field.trim === false ? inputValue : inputValue.trim()
  }
  if (field.type === 'select-multiple' || field.type === 'slot') {
    if (Array.isArray(field.defaultValue)) {
      if (!field.defaultValue.length) return ''
      const filtered = field.defaultValue.filter(item => item && item !== 'all_associated_policies')
      if (!filtered.length) return ''
      return filtered.join(',')
    }
    if (field.type === 'slot') {
      return field.defaultValue || ''
    }
    return ''
  }
  if (field.type === 'text') {
    return field.defaultValue || ''
  }
  return field.defaultValue ?? ''
}

const isDefaultFieldValue = (field, normalizedValue) => {
  const defaultNormalizedValue = normalizeDefaultFieldValue(field)
  if (defaultNormalizedValue === undefined) return false
  return isSameFieldValue(normalizedValue, defaultNormalizedValue)
}

const getOptionLabel = (field, value) => {
  const matched = (field.options || []).find(option => option.value === value)
  return matched ? matched.label : value
}

const getFieldDisplayText = (field, normalizedValue) => {
  if (!normalizedValue) return ''
  if (field.displayValue) return field.displayValue
  if (field.type === 'select-multiple' || field.type === 'slot') {
    return String(normalizedValue)
      .split(',')
      .filter(Boolean)
      .map(item => getOptionLabel(field, item))
      .join(' / ')
  }
  if (field.type === 'select-number') {
    const normalizedText = String(normalizedValue)
    const separatorIndex = normalizedText.indexOf(':')
    if (separatorIndex === -1) return ''
    const operator = normalizedText.slice(0, separatorIndex)
    const displayOperator = selectNumberDisplayOperatorMap[operator]
    if (!displayOperator) return ''
    const value = normalizedText.slice(separatorIndex + 1).trim()
    if (!value) return ''
    return `${displayOperator} ${value}${field.unit ? ` ${field.unit}` : ''}`
  }
  if (field.type === 'select') return getOptionLabel(field, normalizedValue)
  return normalizedValue
}

const buildSortValue = () => {
  const segments = []
  orderFieldList.value.forEach(field => {
    if (!field.value) return
    if (isDefaultFieldValue(field, field.value)) return
    const sortKey = field.sortKey || field.key
    segments.push(field.value === 'desc' ? `-${sortKey}` : sortKey)
  })
  return segments.join(',')
}

const buildFilterObj = () => {
  const filterObj = {}
  ;[...filterFieldList.value, ...otherFieldList.value].forEach(field => {
    const normalizedValue = normalizeFieldValue(field)
    if (!normalizedValue) return
    if (isDefaultFieldValue(field, normalizedValue)) return
    filterObj[field.key] = normalizedValue
  })
  const sortValue = buildSortValue()
  if (sortValue) filterObj.sort = sortValue
  return filterObj
}

const buildFilterStr = () => {
  const segments = []
  ;[...filterFieldList.value, ...otherFieldList.value].forEach(field => {
    const normalizedValue = normalizeFieldValue(field)
    if (!normalizedValue) return
    if (isDefaultFieldValue(field, normalizedValue)) return
    const displayValue = getFieldDisplayText(field, normalizedValue)
    if (!displayValue) return
    segments.push(`${field.label}${props.labelJoiner}${displayValue}`)
  })
  orderFieldList.value.forEach(field => {
    if (!field.value) return
    if (isDefaultFieldValue(field, field.value)) return
    const displayValue = getFieldDisplayText(field, field.value)
    if (!displayValue) return
    segments.push(`${field.label}${props.labelJoiner}${displayValue}`)
  })
  return segments.join(', ')
}

const handleReset = () => {
  filterFieldList.value.forEach(resetFieldValue)
  orderFieldList.value.forEach(resetFieldValue)
  otherFieldList.value.forEach(resetFieldValue)
}

const handleCancel = () => {
  drawerVisible.value = false
  emit('cancel')
}

const handleSubmit = () => {
  const filterObj = buildFilterObj()
  const filterStr = buildFilterStr()
  emit('update:filterCondition', {
    ...filterForm.filter,
    ...filterForm.order,
    ...filterForm.other
  })
  emit('submit', {
    filterObj,
    filterStr,
    rawForm: {
      filter: cloneField(filterForm.filter),
      order: cloneField(filterForm.order),
      other: cloneField(filterForm.other)
    }
  })
  drawerVisible.value = false
}

watch(
  () => [props.visible, props.filterCondition],
  ([visible]) => {
    if (visible) buildFormByCondition()
  },
  { deep: true, immediate: true }
)
</script>

<style lang="scss" scoped>
.advanced-filter-drawer {
  height: 100%;
  padding-bottom: 16px;
  overflow: auto;
}

.field-section + .field-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #d9d9d9;
}

.section-title {
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  margin-bottom: 16px;
  color: #262626;
}

.section-title--with-icon {
  display: flex;
  align-items: center;
  gap: 6px;
}

.section-title--with-icon :deep(.el-icon) {
  font-size: 14px;
  color: #606266;
}

.field-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background: #f5f5f5;
}

.other-field-list {
  gap: 16px;
}

.field-item {
  display: flex;
  align-items: center;
  gap: 0;
}

.field-label {
  display: flex;
  align-items: center;
  width: 180px;
  min-width: 180px;
  height: 36px;
  padding: 0 12px;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 36px;
  color: #595959;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-right: 0;
}

.field-control {
  flex: 1;
  min-width: 180px;
  max-width: 480px;
}

.field-action {
  width: 24px;
  margin-left: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.field-control :deep(.el-input__wrapper),
.field-control :deep(.el-select .el-input__wrapper) {
  border-radius: 0 !important;
}

.field-control :deep(.el-input),
.field-control :deep(.el-select) {
  width: 100%;
}

.field-control :deep(.el-input__inner) {
  height: 36px;
  line-height: 36px;
}

.field-item .field-control :deep(.el-input__wrapper) {
  min-height: 36px;
  border-radius: 0 2px 2px 0 !important;
}

.field-clear-btn {
  width: 16px;
  height: 16px;
  padding: 0 !important;
  min-height: 16px !important;
  border: 0 !important;
  background: transparent !important;
  color: #8c8c8c !important;
}

.field-clear-btn :deep(.el-icon) {
  margin-right: 0 !important;
  font-size: 14px;
}

.field-clear-btn:hover,
.field-clear-btn:focus {
  color: var(--el-color-primary) !important;
  background: transparent !important;
}

.field-remove-btn {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
}

.advanced-add-condition-btn {
  align-self: flex-start;
  color: var(--el-color-primary) !important;
  background: transparent !important;
  border-color: var(--el-color-primary) !important;
}

.advanced-add-condition-btn:hover,
.advanced-add-condition-btn:focus,
.advanced-add-condition-btn:active {
  color: var(--el-color-primary) !important;
  background: transparent !important;
  border-color: var(--el-color-primary) !important;
}

.drawer-footer,
.dialog-footer {
  display: flex;
  justify-content: flex-end;
}

.other-condition-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 16px;
  row-gap: 4px;
}

.other-condition-list :deep(.el-checkbox) {
  margin-right: 0;
  min-width: 0;
}

.other-condition-list :deep(.el-checkbox__label) {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
